import React, { useState, useCallback, useMemo } from "react";

import Button from "../../components/design-system/Button";
import Search from "../../components/design-system/Search";
import Tag from "../../components/design-system/Tag";
import {
  categoryTree,
  type CategoryLevel1,
  type CategoryLevel2,
  type CategoryLevel3,
} from "../../view_models/categoryTree";
import { cn } from "@faire/prototype-playground/lib/utils";

interface FlatItem {
  id: string;
  label: string;
  breadcrumb: string; // e.g. "Home decor › Kitchen & tabletop › Barware"
  level: 1 | 2 | 3;
}

const SKIP_LABELS = new Set([
  "New products",
  "New brands",
  "Bestsellers",
  "Pre-order Fall/Winter '24",
]);

function buildFlatIndex(tree: CategoryLevel1[]): FlatItem[] {
  const items: FlatItem[] = [];
  for (const c1 of tree) {
    items.push({ id: c1.id, label: c1.label, breadcrumb: c1.label, level: 1 });
    for (const c2 of c1.children ?? []) {
      if (SKIP_LABELS.has(c2.label) || c2.label.startsWith("All ")) continue;
      items.push({
        id: c2.id,
        label: c2.label,
        breadcrumb: `${c1.label} › ${c2.label}`,
        level: 2,
      });
      for (const c3 of (c2 as CategoryLevel2 & { children?: CategoryLevel3[] })
        .children ?? []) {
        if (c3.label.startsWith("All ")) continue;
        items.push({
          id: c3.id,
          label: c3.label,
          breadcrumb: `${c1.label} › ${c2.label} › ${c3.label}`,
          level: 3,
        });
      }
    }
  }
  return items;
}

const FLAT_INDEX = buildFlatIndex(categoryTree);

interface BrowseSection {
  id: string;
  label: string;
  chips: { id: string; label: string }[];
}

const BROWSE_FILTER = new Set([
  "home-decor",
  "beauty-wellness",
  "jewelry",
  "food-drink",
  "paper-novelty",
  "kids-baby",
  "apparel",
  "accessories",
  "pets",
]);

function buildBrowseSections(tree: CategoryLevel1[]): BrowseSection[] {
  return tree
    .filter((c1) => BROWSE_FILTER.has(c1.id))
    .map((c1) => ({
      id: c1.id,
      label: c1.label,
      chips: (c1.children ?? [])
        .filter(
          (c2) => !SKIP_LABELS.has(c2.label) && !c2.label.startsWith("All ")
        )
        .slice(0, 7)
        .map((c2) => ({ id: c2.id, label: c2.label })),
    }));
}

const BROWSE_SECTIONS = buildBrowseSections(categoryTree);

const BRAND_COUNTS: Record<string, string> = {
  "home-decor": "2,400+",
  "beauty-wellness": "850+",
  jewelry: "1,100+",
  "food-drink": "900+",
  "paper-novelty": "560+",
  "kids-baby": "720+",
  apparel: "1,800+",
  accessories: "740+",
  pets: "320+",
};

const POPULAR_PICKS: { id: string; label: string }[] = [
  { id: "popular-candles", label: "Candles" },
  { id: "popular-earrings", label: "Earrings" },
  { id: "popular-skincare", label: "Skincare" },
  { id: "popular-snacks", label: "Snacks" },
  { id: "popular-stationery", label: "Stationery" },
  { id: "popular-toys", label: "Toys & games" },
  { id: "popular-bags", label: "Bags" },
];

const ALL_ITEMS_BY_ID: Map<string, { label: string; breadcrumb: string }> =
  new Map();
for (const item of FLAT_INDEX) {
  ALL_ITEMS_BY_ID.set(item.id, {
    label: item.label,
    breadcrumb: item.breadcrumb,
  });
}
for (const pick of POPULAR_PICKS) {
  ALL_ITEMS_BY_ID.set(pick.id, { label: pick.label, breadcrumb: pick.label });
}
for (const section of BROWSE_SECTIONS) {
  if (!ALL_ITEMS_BY_ID.has(section.id)) {
    ALL_ITEMS_BY_ID.set(section.id, {
      label: section.label,
      breadcrumb: section.label,
    });
  }
}

const MAX_SELECTIONS = 10;

function SelectableChip({
  label,
  selected,
  onClick,
  disabled,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={disabled && !selected ? undefined : onClick}
      className={cn("inline-flex items-center rounded-full px-4 py-2 text-[14px] tracking-[0.15px] transition-all duration-150 select-none", selected
    ? "bg-[#333] text-fs-text-primary-inverse"
    : "border border-[#dfe0e1] bg-fs-surface-primary text-[#333] hover:border-[#999]", disabled && !selected ? "cursor-not-allowed opacity-40" : "cursor-pointer")}
      style={{ fontFamily: "'Graphik', sans-serif", lineHeight: "20px" }}
    >
      {selected && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className="mr-1.5"
        >
          <path
            d="M2 6L5 9L10 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {label}
    </button>
  );
}

export default function Variant3() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const totalSelections = selectedIds.size;
  const atLimit = totalSelections >= MAX_SELECTIONS;

  const selectedItems = useMemo(
    () =>
      Array.from(selectedIds)
        .map((id) => {
          const info = ALL_ITEMS_BY_ID.get(id);
          return info
            ? { id, label: info.label, breadcrumb: info.breadcrumb }
            : null;
        })
        .filter(Boolean) as { id: string; label: string; breadcrumb: string }[],
    [selectedIds]
  );

  const isSearching = searchQuery.trim().length > 0;

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = searchQuery.toLowerCase();
    return FLAT_INDEX.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.breadcrumb.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [searchQuery, isSearching]);

  const toggleItem = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (!atLimit) {
          next.add(id);
        }
        return next;
      });
    },
    [atLimit]
  );

  const removeItem = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  return (
    <div className="flex min-h-screen items-start justify-center bg-[#f5f5f5] py-8">
      <div
        className="flex flex-col overflow-hidden rounded-3xl bg-fs-surface-primary shadow-lg"
        style={{ width: 375, height: 812 }}
      >
        {/* Nav bar */}
        <div className="relative flex shrink-0 items-center justify-center px-6 py-4">
          <button
            type="button"
            className="absolute left-2 p-3.5 text-[#333]"
            aria-label="Back"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3L5 8L10 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span
            className="text-base tracking-[0.35em] text-[#333]"
            style={{ fontFamily: "'Graphik', sans-serif", fontWeight: 400 }}
          >
            F A I R E
          </span>
        </div>

        {/* Progress bar */}
        <div className="flex w-full shrink-0">
          <div className="h-1 w-1/4 bg-[#333]" />
          <div className="h-1 flex-1 bg-[#f7f7f7]" />
        </div>

        {/* Sticky header with search + selected tags */}
        <div className="shrink-0 border-b border-[#f0f0f0] bg-fs-surface-primary px-6 pt-6 pb-4">
          <h1
            className="mb-1 text-[26px] leading-[34px] text-[#333]"
            style={{ fontFamily: "'Nantes', serif" }}
          >
            What product categories are you interested in?
          </h1>
          <p
            className="mb-4 text-sm leading-5 tracking-[0.15px] text-[#757575]"
            style={{ fontFamily: "'Graphik', sans-serif" }}
          >
            Search or browse — pick up to {MAX_SELECTIONS}.
          </p>

          <Search
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
          />

          {/* Selected tags */}
          {selectedItems.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {selectedItems.map((item) => (
                <Tag
                  key={item.id}
                  label={item.label}
                  onDismiss={() => removeItem(item.id)}
                  aria-label={`Remove ${item.label}`}
                />
              ))}
            </div>
          )}

          {totalSelections > 0 && (
            <div
              className="mt-2 text-xs tracking-[0.15px] text-[#757575]"
              style={{ fontFamily: "'Graphik', sans-serif" }}
            >
              {totalSelections} of {MAX_SELECTIONS} selected
            </div>
          )}
        </div>

        {/* Scrollable content: search results OR browse sections */}
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-6">
          {isSearching ? (
            <>
              {searchResults.length === 0 ? (
                <div
                  className="py-8 text-center text-sm text-[#757575]"
                  style={{ fontFamily: "'Graphik', sans-serif" }}
                >
                  No categories match "{searchQuery}"
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {searchResults.map((item) => (
                    <SelectableChip
                      key={item.id}
                      label={item.label}
                      selected={selectedIds.has(item.id)}
                      onClick={() => toggleItem(item.id)}
                      disabled={atLimit}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Popular picks row */}
              <div className="mb-5">
                <div className="mb-2 flex items-center gap-1.5">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    className="text-[#999]"
                  >
                    <path
                      d="M6 1L7.5 4.1L11 4.5L8.5 6.9L9.2 10.4L6 8.7L2.8 10.4L3.5 6.9L1 4.5L4.5 4.1L6 1Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span
                    className="text-[11px] font-medium tracking-[0.8px] text-[#757575] uppercase"
                    style={{ fontFamily: "'Graphik', sans-serif" }}
                  >
                    Popular with stores like yours
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {POPULAR_PICKS.map((pick) => (
                    <SelectableChip
                      key={pick.id}
                      label={pick.label}
                      selected={selectedIds.has(pick.id)}
                      onClick={() => toggleItem(pick.id)}
                      disabled={atLimit}
                    />
                  ))}
                </div>
              </div>

              <div className="mb-4 h-px bg-[#f0f0f0]" />

              {/* Browse mode: C1 sections with top subcategory chips */}
              {BROWSE_SECTIONS.map((section) => (
                <div key={section.id} className="mb-5">
                  <button
                    type="button"
                    onClick={() => toggleItem(section.id)}
                    className="mb-2 flex w-full items-center gap-2 text-left"
                  >
                    <span
                      className="text-[11px] font-medium tracking-[0.8px] text-[#757575] uppercase"
                      style={{ fontFamily: "'Graphik', sans-serif" }}
                    >
                      {section.label}
                    </span>
                    {BRAND_COUNTS[section.id] && (
                      <span
                        className="text-[10px] text-[#bbb]"
                        style={{ fontFamily: "'Graphik', sans-serif" }}
                      >
                        · {BRAND_COUNTS[section.id]} brands
                      </span>
                    )}
                    <span className="flex-1" />
                    {selectedIds.has(section.id) && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="#333"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>

                  <div className="flex flex-wrap gap-1.5">
                    {section.chips.map((chip) => (
                      <SelectableChip
                        key={chip.id}
                        label={chip.label}
                        selected={selectedIds.has(chip.id)}
                        onClick={() => toggleItem(chip.id)}
                        disabled={atLimit}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Sticky bottom */}
        <div className="shrink-0 border-t border-[#f0f0f0] bg-fs-surface-primary px-6 py-4">
          <Button
            variant="primary"
            disabled={totalSelections === 0}
            className="w-full"
            onClick={() => {
              const items = selectedItems.map((i) => i.breadcrumb);
              alert(`Selected:\n${items.join("\n")}`);
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
